import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Headset, Search, TicketCheck, Clock, AlertCircle, CheckCircle2,
  Send, MessageSquare, Filter, ChevronDown, UserCheck,
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";

const categoryLabels: Record<string, string> = {
  general: "عام",
  login: "تسجيل الدخول",
  upload: "رفع الملفات",
  live: "البث المباشر",
  billing: "الفواتير والدفع",
  technical: "مشكلة تقنية",
};

const priorityLabels: Record<string, string> = {
  low: "منخفضة",
  medium: "متوسطة",
  high: "عالية",
  urgent: "عاجلة",
};

const priorityColors: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  high: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  urgent: "bg-destructive/10 text-destructive",
};

const statusLabels: Record<string, string> = {
  open: "مفتوحة",
  in_progress: "قيد المعالجة",
  resolved: "تم الحل",
  closed: "مغلقة",
};

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  closed: "bg-muted text-muted-foreground",
};

const AdminSupport = () => {
  const { user, profile, roles } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [replyText, setReplyText] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Fetch support staff for assignment
  const { data: supportStaff } = useQuery({
    queryKey: ["support-staff-list"],
    queryFn: async () => {
      const { data: roleData, error } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("role", ["support", "call_center"] as any);
      if (error) throw error;
      if (!roleData?.length) return [];
      const userIds = [...new Set(roleData.map(r => r.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      return profiles?.map(p => ({
        ...p,
        roles: roleData.filter(r => r.user_id === p.user_id).map(r => r.role),
      })) || [];
    },
  });

  // Fetch tickets
  const { data: tickets, isLoading } = useQuery({
    queryKey: ["admin-support-tickets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*, nurseries:nursery_id(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;

      // Fetch creator profiles
      const userIds = [...new Set((data || []).map((t: any) => t.created_by))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      
      const profileMap = new Map(profiles?.map((p: any) => [p.user_id, p.full_name]) || []);
      
      return (data || []).map((t: any) => ({
        ...t,
        creator_name: profileMap.get(t.created_by) || "مستخدم",
      }));
    },
  });

  // Fetch replies for selected ticket
  const { data: replies } = useQuery({
    queryKey: ["ticket-replies", selectedTicket?.id],
    queryFn: async () => {
      if (!selectedTicket?.id) return [];
      const { data, error } = await supabase
        .from("ticket_replies")
        .select("*")
        .eq("ticket_id", selectedTicket.id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedTicket?.id,
  });

  // Realtime replies
  useEffect(() => {
    if (!selectedTicket?.id) return;
    const channel = supabase
      .channel(`ticket-${selectedTicket.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "ticket_replies",
        filter: `ticket_id=eq.${selectedTicket.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["ticket-replies", selectedTicket.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedTicket?.id, queryClient]);

  // Scroll to bottom on new replies
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [replies]);

  // Send reply
  const sendReply = useMutation({
    mutationFn: async () => {
      if (!replyText.trim() || !selectedTicket?.id || !user?.id) return;
      const { error } = await supabase.from("ticket_replies").insert({
        ticket_id: selectedTicket.id,
        sender_id: user.id,
        sender_name: profile?.full_name || "الدعم الفني",
        sender_role: "support",
        content: replyText.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: ["ticket-replies", selectedTicket?.id] });
    },
    onError: () => toast.error("خطأ في إرسال الرد"),
  });

  // Update ticket status
  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updates: any = { status };
      if (status === "resolved") updates.resolved_at = new Date().toISOString();
      if (status === "in_progress" && !selectedTicket?.assigned_to) {
        updates.assigned_to = user?.id;
      }
      const { error } = await supabase.from("support_tickets").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تحديث الحالة");
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      if (selectedTicket) {
        setSelectedTicket((prev: any) => prev ? { ...prev, status: prev.status } : null);
      }
    },
  });

  // Assign ticket to staff
  const assignTicket = useMutation({
    mutationFn: async ({ ticketId, staffUserId }: { ticketId: string; staffUserId: string | null }) => {
      const { error } = await supabase
        .from("support_tickets")
        .update({ assigned_to: staffUserId })
        .eq("id", ticketId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      const staffName = supportStaff?.find(s => s.user_id === vars.staffUserId)?.full_name;
      toast.success(vars.staffUserId ? `تم تعيين التذكرة لـ ${staffName}` : "تم إلغاء التعيين");
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      setSelectedTicket((prev: any) => prev ? { ...prev, assigned_to: vars.staffUserId } : null);
    },
    onError: () => toast.error("فشل تعيين التذكرة"),
  });

  // Stats
  const stats = {
    total: tickets?.length || 0,
    open: tickets?.filter((t: any) => t.status === "open").length || 0,
    in_progress: tickets?.filter((t: any) => t.status === "in_progress").length || 0,
    resolved: tickets?.filter((t: any) => t.status === "resolved" || t.status === "closed").length || 0,
  };

  // Filtered tickets
  const filtered = tickets?.filter((t: any) => {
    if (search && !t.subject.includes(search) && !t.creator_name?.includes(search) && !t.ticket_number?.toString().includes(search)) return false;
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterPriority !== "all" && t.priority !== filterPriority) return false;
    return true;
  }) || [];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Headset className="w-6 h-6 text-primary" />
          الدعم الفني والتذاكر
        </h1>
        <p className="text-muted-foreground text-sm mt-1">إدارة استفسارات ومشاكل الحضانات وأولياء الأمور</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><TicketCheck className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي التذاكر</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10"><AlertCircle className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">مفتوحة</p>
              <p className="text-2xl font-bold">{stats.open}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><Clock className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">قيد المعالجة</p>
              <p className="text-2xl font-bold">{stats.in_progress}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">تم حلها</p>
              <p className="text-2xl font-bold">{stats.resolved}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث بالموضوع أو رقم التذكرة..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="الحالة" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="open">مفتوحة</SelectItem>
                <SelectItem value="in_progress">قيد المعالجة</SelectItem>
                <SelectItem value="resolved">تم الحل</SelectItem>
                <SelectItem value="closed">مغلقة</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-36"><SelectValue placeholder="الأولوية" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأولويات</SelectItem>
                <SelectItem value="urgent">عاجلة</SelectItem>
                <SelectItem value="high">عالية</SelectItem>
                <SelectItem value="medium">متوسطة</SelectItem>
                <SelectItem value="low">منخفضة</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-5 h-5" />
            التذاكر ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <TicketCheck className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">لا توجد تذاكر {filterStatus !== "all" ? "بهذه الحالة" : ""}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>الموضوع</TableHead>
                    <TableHead>المرسل</TableHead>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>التصنيف</TableHead>
                    <TableHead>الأولوية</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>المعيّن</TableHead>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>إجراء</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t: any) => (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedTicket(t)}>
                      <TableCell className="font-mono text-xs">#{t.ticket_number}</TableCell>
                      <TableCell className="font-medium max-w-[200px] truncate">{t.subject}</TableCell>
                      <TableCell>{t.creator_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{(t as any).nurseries?.name || "—"}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{categoryLabels[t.category] || t.category}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[t.priority]}`}>
                          {priorityLabels[t.priority]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[t.status]}`}>
                          {statusLabels[t.status]}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {t.assigned_to
                          ? <span className="text-green-600 font-medium">{supportStaff?.find((s: any) => s.user_id === t.assigned_to)?.full_name || "معيّن"}</span>
                          : <span className="text-muted-foreground">—</span>
                        }
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(t.created_at), "dd MMM HH:mm", { locale: ar })}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedTicket(t); }}>
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(o) => !o && setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                تذكرة #{selectedTicket?.ticket_number}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedTicket?.status]}`}>
                {statusLabels[selectedTicket?.status]}
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="flex flex-col gap-4 flex-1 min-h-0">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">الموضوع</p>
                  <p className="font-medium">{selectedTicket.subject}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">المرسل</p>
                  <p className="font-medium">{selectedTicket.creator_name}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">التصنيف</p>
                  <p className="font-medium">{categoryLabels[selectedTicket.category]}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">الأولوية</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[selectedTicket.priority]}`}>
                    {priorityLabels[selectedTicket.priority]}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="p-3 rounded-lg bg-muted/30 border text-sm">
                <p className="text-xs text-muted-foreground mb-1">الوصف</p>
                <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Status Controls */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">تغيير الحالة:</span>
                {["open", "in_progress", "resolved", "closed"].map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={selectedTicket.status === s ? "default" : "outline"}
                    className="text-xs h-7"
                    onClick={() => {
                      updateStatus.mutate({ id: selectedTicket.id, status: s });
                      setSelectedTicket((prev: any) => prev ? { ...prev, status: s } : null);
                    }}
                  >
                    {statusLabels[s]}
                  </Button>
                ))}
              </div>

              {/* Assign to Staff */}
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">تعيين لـ:</span>
                <Select
                  value={selectedTicket.assigned_to || "unassigned"}
                  onValueChange={(val) => {
                    const staffId = val === "unassigned" ? null : val;
                    assignTicket.mutate({ ticketId: selectedTicket.id, staffUserId: staffId });
                  }}
                >
                  <SelectTrigger className="w-48 h-8 text-xs">
                    <SelectValue placeholder="غير معيّن" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">غير معيّن</SelectItem>
                    {supportStaff?.map((s: any) => (
                      <SelectItem key={s.user_id} value={s.user_id}>
                        {s.full_name || "بدون اسم"} ({s.roles?.includes("support") ? "دعم فني" : "كول سنتر"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTicket.assigned_to && (
                  <span className="text-xs text-green-600 font-medium">
                    ✓ {supportStaff?.find((s: any) => s.user_id === selectedTicket.assigned_to)?.full_name || "معيّن"}
                  </span>
                )}
              </div>

              <div className="flex-1 min-h-0 border rounded-lg flex flex-col">
                <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[300px]">
                  {!replies?.length ? (
                    <p className="text-center text-sm text-muted-foreground py-8">لا توجد ردود بعد. أرسل أول رد.</p>
                  ) : (
                    replies.map((r: any) => (
                      <div
                        key={r.id}
                        className={`flex ${r.sender_role === "support" ? "justify-start" : "justify-end"}`}
                      >
                        <div className={`max-w-[75%] rounded-lg p-3 text-sm ${
                          r.sender_role === "support"
                            ? "bg-primary/10 text-foreground"
                            : "bg-muted text-foreground"
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-xs">{r.sender_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(r.created_at), "HH:mm", { locale: ar })}
                            </span>
                          </div>
                          <p className="whitespace-pre-wrap">{r.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Reply Input */}
                <div className="border-t p-3 flex gap-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="اكتب ردك هنا..."
                    className="resize-none min-h-[44px] max-h-[100px]"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (replyText.trim()) sendReply.mutate();
                      }
                    }}
                  />
                  <Button
                    size="icon"
                    onClick={() => replyText.trim() && sendReply.mutate()}
                    disabled={!replyText.trim() || sendReply.isPending}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupport;
